import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { SearchFilter } from '../components/ui/SearchFilter';
import { Card } from '../components/ui/Card';
import { Plus, Edit2, Tag, Copy, ToggleLeft, ToggleRight, RotateCcw, ChevronRight, ChevronLeft } from 'lucide-react';
import { useAuth } from '../context/AuthContext';
import {
  listPromoCodes,
  createPromoCode,
  bulkGeneratePromoCodes,
  activatePromoCode,
  deactivatePromoCode,
  listRestaurants,
  listGroceries,
  getResortLocations,
  getPromoCodeOrders
} from '../services/resortAdminApi';
import { MultiSelect } from '../components/ui/MultiSelect';
import { PromoTabs } from '../components/banners/PromoTabs';
import { validatePromoCodeForm, getValidationErrorMessage } from '../utils/promoCodeValidation';

interface MerchantInfo {
  type: string;
  name: string;
  display_name?: string;
}

interface PromoCode {
  name: string;
  code: string;
  discount_type: 'fixed' | 'percentage';
  discount_value: number;
  min_order_amount: number;
  max_order_amount: number;
  restaurant?: string;
  restaurant_name?: string;
  merchants_display?: string;
  resorts_display?: string;
  merchants?: MerchantInfo[];
  resorts?: { name: string; display_name: string }[];
  max_uses_per_user: number;
  start_date: string;
  end_date: string;
  status: 'Active' | 'Inactive';
  is_redeemed: number;
  redeemed_by_order?: string;
  batch_id?: string;
}

interface MerchantOption {
  value: string;
  label: string;
  group: string;
  type: string;
}

interface ResortOption {
  value: string;
  label: string;
}

export const PromoCodes: React.FC = () => {
  const navigate = useNavigate();
  const { isReadOnly } = useAuth();
  const [searchTerm, setSearchTerm] = useState('');
  const [promoCodes, setPromoCodes] = useState<PromoCode[]>([]);
  const [loading, setLoading] = useState(false);
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(25);
  const [total, setTotal] = useState(0);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [showBulkModal, setShowBulkModal] = useState(false);
  const [error, setError] = useState('');
  const [modalError, setModalError] = useState('');
  const [bulkModalError, setBulkModalError] = useState('');
  const [success, setSuccess] = useState('');

  // Expandable rows
  const [expandedPromo, setExpandedPromo] = useState<string | null>(null);
  const [expandedOrders, setExpandedOrders] = useState<any[]>([]);
  const [expandedLoading, setExpandedLoading] = useState(false);

  // Filters
  const [statusFilter, setStatusFilter] = useState<string>('');
  const [typeFilter, setTypeFilter] = useState<string>('');
  const [redeemedFilter, setRedeemedFilter] = useState<string>('');
  const [startDateFilter, setStartDateFilter] = useState<string>('');
  const [endDateFilter, setEndDateFilter] = useState<string>('');

  // Merchant and resort options for dropdowns
  const [merchantOptions, setMerchantOptions] = useState<MerchantOption[]>([]);
  const [resortOptions, setResortOptions] = useState<ResortOption[]>([]);

  // Single code form
  const [formData, setFormData] = useState({
    code: '',
    discount_type: 'fixed' as 'fixed' | 'percentage',
    discount_value: '' as number | '',
    min_order_amount: 0,
    max_order_amount: 0,
    selectedMerchants: [] as string[],
    selectedResorts: [] as string[],
    max_uses_per_user: 0 as number | '',
    start_date: '',
    end_date: '',
    status: 'Active' as 'Active' | 'Inactive'
  });

  // Bulk generate form
  const [bulkFormData, setBulkFormData] = useState({
    count: 10,
    discount_type: 'fixed' as 'fixed' | 'percentage',
    discount_value: '' as number | '',
    min_order_amount: 0,
    max_order_amount: 0,
    selectedMerchants: [] as string[],
    selectedResorts: [] as string[],
    max_uses_per_user: 0 as number | '',
    start_date: '',
    end_date: '',
    status: 'Active' as 'Active' | 'Inactive',
    code_prefix: ''
  });

  // Load promo codes
  const loadPromoCodes = async () => {
    setLoading(true);
    try {
      const filters: any = {};
      if (statusFilter) filters.status = statusFilter;
      if (typeFilter) filters.discount_type = typeFilter;
      if (redeemedFilter !== '') filters.is_redeemed = redeemedFilter === 'true';
      if (startDateFilter) filters.start_date = startDateFilter;
      if (endDateFilter) filters.end_date = endDateFilter;

      const result = await listPromoCodes(
        Object.keys(filters).length > 0 ? filters : undefined,
        searchTerm || undefined,
        page,
        pageSize
      );
      
      if (result && result.data) {
        setPromoCodes(result.data);
        setTotal(result.pagination?.total || 0);
      }
    } catch (error: any) {
      console.error('Error loading promo codes:', error);
      setError(error.message || 'Failed to load promo codes');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadPromoCodes();
  }, [page, pageSize, searchTerm, statusFilter, typeFilter, redeemedFilter, startDateFilter, endDateFilter]);

  // Load merchant and resort options on mount
  useEffect(() => {
    const loadOptions = async () => {
      try {
        // Load restaurants and groceries in parallel
        const [restaurantsResult, groceriesResult, resortsResult] = await Promise.all([
          listRestaurants(undefined, 1, 200).catch(() => null),
          listGroceries(undefined, 1, 200).catch(() => null),
          getResortLocations().catch(() => null),
        ]);

        const merchants: MerchantOption[] = [];

        // Add restaurants
        const restaurantsData = restaurantsResult?.data || restaurantsResult || [];
        restaurantsData.forEach((r: any) => {
          merchants.push({
            value: `Restaurant:${r.name}`,
            label: r.restaurant_name || r.name,
            group: 'Restaurants',
            type: 'Restaurant',
          });
        });

        // Add groceries
        const groceriesData = groceriesResult?.data || groceriesResult || [];
        groceriesData.forEach((g: any) => {
          merchants.push({
            value: `Grocery:${g.name}`,
            label: g.grocery_name || g.name,
            group: 'Groceries',
            type: 'Grocery',
          });
        });

        setMerchantOptions(merchants);

        // Load resorts
        const resortLocations = resortsResult?.resort_locations || [];
        setResortOptions(resortLocations.map((r: any) => ({
          value: r.value,
          label: r.label || r.value,
        })));
      } catch (err) {
        console.error('Failed to load merchant/resort options:', err);
      }
    };
    loadOptions();
  }, []);

  // Table columns
  const columns = [
    {
      key: 'expand',
      header: '',
      render: (_: any, row: PromoCode) => (
        <button
          onClick={(e) => { e.stopPropagation(); handleExpandPromo(row.name); }}
          className="text-gray-500 hover:text-gray-700"
        >
          <ChevronRight className={`h-4 w-4 transition-transform ${expandedPromo === row.name ? 'rotate-90' : ''}`} />
        </button>
      )
    },
    {
      key: 'code',
      header: 'Code',
      render: (value: string) => (
        <span className="font-mono font-medium text-indigo-600">{value}</span>
      )
    },
    {
      key: 'discount_type',
      header: 'Type',
      render: (value: string, row: PromoCode) => (
        <span>
          {value === 'fixed' ? `€${row.discount_value}` : `${row.discount_value}%`}
        </span>
      )
    },
    {
      key: 'min_order_amount',
      header: 'Min. Order',
      render: (value: number) => value > 0 ? `€${value}` : '-'
    },
    {
      key: 'merchants_display',
      header: 'Merchant',
      render: (value: string | null, row: PromoCode) => {
        const parts: string[] = [];
        if (value) parts.push(value);
        if (row.resorts_display) parts.push(`Resorts: ${row.resorts_display}`);
        return parts.length > 0 ? parts.join('; ') : 'All';
      }
    },
    {
      key: 'start_date',
      header: 'Valid From'
    },
    {
      key: 'end_date',
      header: 'Valid Until'
    },
    {
      key: 'status',
      header: 'Status',
      render: (value: string) => (
        <span className={`px-2 py-1 text-xs rounded-full ${
          value === 'Active' ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'
        }`}>
          {value}
        </span>
      )
    },
    {
      key: 'total_uses',
      header: 'Uses',
      render: (value: number) => (
        <span className="font-medium">{value || 0}</span>
      )
    },
    {
      key: 'actions',
      header: 'Actions',
      render: (_: any, row: PromoCode) => (
        <div className="flex space-x-2">
          <button
            onClick={() => navigate(`/promo-codes/${row.name}`)}
            className="text-indigo-600 hover:text-indigo-900"
            title="Edit"
          >
            <Edit2 className="h-4 w-4" />
          </button>
          <button
            onClick={() => handleToggleStatus(row)}
            className={`${row.status === 'Active' ? 'text-green-600 hover:text-green-900' : 'text-orange-600 hover:text-orange-900'} disabled:opacity-50 disabled:cursor-not-allowed`}
            title={row.status === 'Active' ? 'Deactivate' : 'Activate'}
            disabled={isReadOnly}
          >
            {row.status === 'Active' ? <ToggleRight className="h-4 w-4" /> : <ToggleLeft className="h-4 w-4" />}
          </button>
          <button
            onClick={() => copyToClipboard(row.code)}
            className="text-gray-600 hover:text-gray-900"
            title="Copy code"
          >
            <Copy className="h-4 w-4" />
          </button>
        </div>
      )
    }
  ];

  const copyToClipboard = (text: string) => {
    navigator.clipboard.writeText(text);
    setSuccess(`Code "${text}" copied to clipboard`);
    setTimeout(() => setSuccess(''), 2000);
  };

  const handleToggleStatus = async (promo: PromoCode) => {
    try {
      if (promo.status === 'Active') {
        await deactivatePromoCode(promo.name);
      } else {
        await activatePromoCode(promo.name);
      }
      loadPromoCodes();
      setSuccess(`Promo code ${promo.status === 'Active' ? 'deactivated' : 'activated'} successfully`);
    } catch (error: any) {
      setError(error.message || 'Failed to update status');
    }
  };

  const handleResetFilters = () => {
    setStatusFilter('');
    setTypeFilter('');
    setRedeemedFilter('');
    setStartDateFilter('');
    setEndDateFilter('');
    setSearchTerm('');
    setPage(1); // Reset to first page
  };

  const handleExpandPromo = async (promoName: string) => {
    if (expandedPromo === promoName) {
      setExpandedPromo(null);
      setExpandedOrders([]);
      return;
    }
    setExpandedPromo(promoName);
    setExpandedLoading(true);
    try {
      const orders = await getPromoCodeOrders(promoName);
      setExpandedOrders(orders || []);
    } catch (e) {
      setExpandedOrders([]);
    } finally {
      setExpandedLoading(false);
    }
  };

  const handleCreateSingle = async (e: React.FormEvent) => {
    e.preventDefault();
    setModalError('');
    
    // Frontend validation
    const validationErrors = validatePromoCodeForm(formData, false);
    if (validationErrors.length > 0) {
      setModalError(getValidationErrorMessage(validationErrors));
      return;
    }

    // At this point, validation passed, so discount_value must be a valid number
    if (formData.discount_value === '' || formData.discount_value === undefined) {
      setModalError('The discount value is required and cannot be 0');
      return;
    }

    try {
      // Convert selected merchant values to API format
      const merchants = formData.selectedMerchants.length > 0
        ? formData.selectedMerchants.map(v => {
            const [type, name] = v.split(':');
            return { type, name };
          })
        : null;
      const resorts = formData.selectedResorts.length > 0 ? formData.selectedResorts : null;

      const result = await createPromoCode({
        code: formData.code,
        discount_type: formData.discount_type,
        discount_value: formData.discount_value as number,
        min_order_amount: formData.min_order_amount,
        max_order_amount: formData.max_order_amount,
        restaurant: null,
        merchants,
        resorts,
        max_uses_per_user: formData.max_uses_per_user === '' ? 0 : formData.max_uses_per_user,
        start_date: formData.start_date || null,
        end_date: formData.end_date || null,
        status: formData.status,
      });

      // Check if backend returned an error response
      if (result && result.ok === false) {
        const errorCode = result.error_code;
        const errorMessage = result.error_message || 'Failed to create promo code';

        if (errorCode === 'PROMO_CODE_ALREADY_SET') {
          setModalError(`This promo code "${formData.code.toUpperCase()}" already exists. Please use a different code.`);
        } else {
          setModalError(errorMessage);
        }
        return;
      }

      setSuccess('Promo code created successfully');
      setModalError('');
      setShowCreateModal(false);
      setFormData({
        code: '',
        discount_type: 'fixed',
        discount_value: '',
        min_order_amount: 0,
        max_order_amount: 0,
        selectedMerchants: [],
        selectedResorts: [],
        max_uses_per_user: 0,
        start_date: '',
        end_date: '',
        status: 'Active'
      });
      loadPromoCodes();
    } catch (error: any) {
      // Extract user-friendly error message
      let errorMessage = 'Failed to create promo code';
      if (error.message) {
        errorMessage = error.message;
      } else if (error.response?.data?._error_message) {
        errorMessage = error.response.data._error_message;
      } else if (error.response?.data?.exception) {
        errorMessage = error.response.data.exception;
      }
      setModalError(errorMessage);
    }
  };

  const handleBulkGenerate = async (e: React.FormEvent) => {
    e.preventDefault();
    setBulkModalError('');

    // Frontend validation
    const validationErrors = validatePromoCodeForm(bulkFormData, false);
    if (validationErrors.length > 0) {
      setBulkModalError(getValidationErrorMessage(validationErrors));
      return;
    }

    // At this point, validation passed, so discount_value must be a valid number
    if (bulkFormData.discount_value === '' || bulkFormData.discount_value === undefined) {
      setBulkModalError('The discount value is required and cannot be 0');
      return;
    }

    try {
      // Convert selected merchant values to API format
      const merchants = bulkFormData.selectedMerchants.length > 0
        ? bulkFormData.selectedMerchants.map(v => {
            const [type, name] = v.split(':');
            return { type, name };
          })
        : null;
      const resorts = bulkFormData.selectedResorts.length > 0 ? bulkFormData.selectedResorts : null;

      const result = await bulkGeneratePromoCodes({
        count: bulkFormData.count,
        discount_type: bulkFormData.discount_type,
        discount_value: bulkFormData.discount_value as number,
        min_order_amount: bulkFormData.min_order_amount,
        max_order_amount: bulkFormData.max_order_amount,
        restaurant: null,
        merchants,
        resorts,
        max_uses_per_user: bulkFormData.max_uses_per_user === '' ? 0 : bulkFormData.max_uses_per_user,
        start_date: bulkFormData.start_date || null,
        end_date: bulkFormData.end_date || null,
        status: bulkFormData.status,
        code_prefix: bulkFormData.code_prefix,
      });
      setSuccess(`Successfully generated ${result.count} ${result.count === 1 ? 'promo code' : 'promo codes'}`);
      setBulkModalError('');
      setShowBulkModal(false);
      setBulkFormData({
        count: 10,
        discount_type: 'fixed',
        discount_value: '',
        min_order_amount: 0,
        max_order_amount: 0,
        selectedMerchants: [],
        selectedResorts: [],
        max_uses_per_user: 0,
        start_date: '',
        end_date: '',
        status: 'Active',
        code_prefix: ''
      });
      loadPromoCodes();
    } catch (error: any) {
      // Extract user-friendly error message
      let errorMessage = 'Failed to generate promo codes';
      if (error.message) {
        errorMessage = error.message;
      } else if (error.response?.data?._error_message) {
        errorMessage = error.response.data._error_message;
      } else if (error.response?.data?.exception) {
        errorMessage = error.response.data.exception;
      }
      setBulkModalError(errorMessage);
    }
  };

  return (
    <div className="space-y-6">
      <PromoTabs />
      {/* Header */}
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-2xl font-semibold text-gray-800">Promo Codes</h1>
          <p className="mt-1 text-sm text-gray-600">Manage discount codes for food and grocery orders</p>
        </div>
        <div className="flex space-x-2">
          <button
            onClick={() => {
              setBulkModalError('');
              setShowBulkModal(true);
            }}
            disabled={isReadOnly}
            className="inline-flex items-center px-4 py-2 border border-alps-primary shadow-sm text-sm font-medium rounded-md text-alps-primary bg-white hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            <Tag className="h-4 w-4 mr-2" />
            Bulk Generate
          </button>
          <button
            onClick={() => {
              setModalError('');
              setShowCreateModal(true);
            }}
            disabled={isReadOnly}
            className="inline-flex items-center px-4 py-2 border border-transparent shadow-sm text-sm font-medium rounded-md text-white bg-alps-primary hover:bg-alps-primary-dark disabled:opacity-50 disabled:cursor-not-allowed"
          >
            <Plus className="h-4 w-4 mr-2" />
            Create Code
          </button>
        </div>
      </div>

      {/* Alerts */}
      {error && (
        <div className="bg-red-50 border-l-4 border-red-500 p-4">
          <p className="text-sm text-red-700">{error}</p>
        </div>
      )}
      {success && (
        <div className="bg-green-50 border-l-4 border-green-500 p-4">
          <p className="text-sm text-green-700">{success}</p>
        </div>
      )}

      {/* Filters */}
      <Card>
        <div className="flex flex-wrap gap-4 mb-4">
          <select
            value={statusFilter}
            onChange={(e) => setStatusFilter(e.target.value)}
            className="border border-gray-300 rounded-md px-3 py-2 text-sm"
          >
            <option value="">All Statuses</option>
            <option value="Active">Active</option>
            <option value="Inactive">Inactive</option>
          </select>
          <select
            value={typeFilter}
            onChange={(e) => setTypeFilter(e.target.value)}
            className="border border-gray-300 rounded-md px-3 py-2 text-sm"
          >
            <option value="">All Types</option>
            <option value="fixed">Fixed Amount</option>
            <option value="percentage">Percentage</option>
          </select>
          <select
            value={redeemedFilter}
            onChange={(e) => setRedeemedFilter(e.target.value)}
            className="border border-gray-300 rounded-md px-3 py-2 text-sm"
          >
            <option value="">All</option>
            <option value="false">Not Redeemed</option>
            <option value="true">Redeemed</option>
          </select>
          <input
            type="date"
            value={startDateFilter}
            onChange={(e) => setStartDateFilter(e.target.value)}
            className="border border-gray-300 rounded-md px-3 py-2 text-sm"
            placeholder="Valid from"
          />
          <input
            type="date"
            value={endDateFilter}
            onChange={(e) => setEndDateFilter(e.target.value)}
            className="border border-gray-300 rounded-md px-3 py-2 text-sm"
            placeholder="Valid until"
          />
          <button
            onClick={handleResetFilters}
            className="inline-flex items-center px-4 py-2 border border-gray-300 rounded-md shadow-sm text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-alps-primary"
          >
            <RotateCcw className="h-4 w-4 mr-2" />
            Reset
          </button>
        </div>
        <SearchFilter
          placeholder="Search by code..."
          onSearch={setSearchTerm}
          initialValue={searchTerm}
        />
        {loading ? (
          <div className="bg-white rounded-lg shadow overflow-hidden">
            <div className="flex justify-center items-center h-64">
              <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-indigo-500"></div>
            </div>
          </div>
        ) : (
          <div className="bg-white rounded-lg shadow overflow-hidden">
            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-200">
                <thead className="bg-gray-50">
                  <tr>
                    {columns.map(column => (
                      <th key={column.key} scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        {column.header}
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200 align-top">
                  {promoCodes.length > 0 ? promoCodes.map((row, rowIndex) => (
                    <React.Fragment key={rowIndex}>
                      <tr className="hover:bg-gray-50 align-top">
                        {columns.map(column => (
                          <td key={`${rowIndex}-${column.key}`} className="px-6 py-4 whitespace-nowrap text-sm text-gray-500 align-top">
                            {column.render ? (column.render as any)((row as any)[column.key], row) : (row as any)[column.key]}
                          </td>
                        ))}
                      </tr>
                      {expandedPromo === row.name && (
                        <tr>
                          <td colSpan={columns.length} className="px-6 py-3 bg-gray-50">
                            {expandedLoading ? (
                              <div className="text-sm text-gray-500 py-2">Loading orders...</div>
                            ) : expandedOrders.length === 0 ? (
                              <div className="text-sm text-gray-500 py-2">No orders found for this promo code</div>
                            ) : (
                              <div className="overflow-x-auto">
                                <table className="min-w-full text-sm">
                                  <thead>
                                    <tr className="text-xs text-gray-500 uppercase">
                                      <th className="px-3 py-2 text-left">Date</th>
                                      <th className="px-3 py-2 text-left">Order ID</th>
                                      <th className="px-3 py-2 text-left">Resort</th>
                                      <th className="px-3 py-2 text-left">Merchant</th>
                                      <th className="px-3 py-2 text-right">Order Total</th>
                                      <th className="px-3 py-2 text-right">Discounted Total</th>
                                    </tr>
                                  </thead>
                                  <tbody>
                                    {expandedOrders.map((order: any, idx: number) => (
                                      <tr key={idx} className="border-t border-gray-200">
                                        <td className="px-3 py-2 text-gray-700">{order.order_date}</td>
                                        <td className="px-3 py-2 text-gray-600">{order.order_id}</td>
                                        <td className="px-3 py-2 text-gray-600">{order.resort_name || '-'}</td>
                                        <td className="px-3 py-2 text-gray-600">{order.merchant_name || '-'}</td>
                                        <td className="px-3 py-2 text-right text-gray-700">&euro;{order.order_total?.toFixed(2)}</td>
                                        <td className="px-3 py-2 text-right font-medium text-gray-900">&euro;{order.discounted_total?.toFixed(2)}</td>
                                      </tr>
                                    ))}
                                  </tbody>
                                </table>
                              </div>
                            )}
                          </td>
                        </tr>
                      )}
                    </React.Fragment>
                  )) : (
                    <tr>
                      <td colSpan={columns.length} className="px-6 py-12 text-center text-sm text-gray-500">
                        No promo codes found
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>

            {/* Pagination */}
            {total > pageSize && (() => {
              const totalPages = Math.ceil(total / pageSize);
              const startItem = (page - 1) * pageSize + 1;
              const endItem = Math.min(page * pageSize, total);
              return (
                <div className="bg-white px-4 py-3 flex items-center justify-between border-t border-gray-200 sm:px-6">
                  <div className="hidden sm:flex-1 sm:flex sm:items-center sm:justify-between">
                    <p className="text-sm text-gray-700">
                      Showing <span className="font-medium">{startItem}</span> to <span className="font-medium">{endItem}</span> of{' '}
                      <span className="font-medium">{total}</span> results
                    </p>
                    <div className="flex items-center space-x-2">
                      <div className="flex items-center">
                        <label className="text-sm text-gray-700 mr-2">Show:</label>
                        <select
                          value={pageSize}
                          onChange={(e) => setPageSize(Number(e.target.value))}
                          className="block w-20 px-3 py-1 border border-gray-300 rounded-md text-sm"
                        >
                          <option value={10}>10</option>
                          <option value={25}>25</option>
                          <option value={50}>50</option>
                          <option value={100}>100</option>
                        </select>
                      </div>
                      <div className="flex items-center space-x-1">
                        <button onClick={() => setPage(page - 1)} disabled={page <= 1}
                          className="inline-flex items-center px-2 py-2 border border-gray-300 text-sm rounded-md text-gray-500 bg-white hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed">
                          <ChevronLeft className="h-4 w-4" />
                        </button>
                        {Array.from({ length: Math.min(5, totalPages) }, (_, i) => {
                          let pageNum;
                          if (totalPages <= 5) pageNum = i + 1;
                          else if (page <= 3) pageNum = i + 1;
                          else if (page >= totalPages - 2) pageNum = totalPages - 4 + i;
                          else pageNum = page - 2 + i;
                          return (
                            <button key={pageNum} onClick={() => setPage(pageNum)}
                              className={`inline-flex items-center px-4 py-2 text-sm font-medium border rounded-md ${
                                pageNum === page ? 'bg-indigo-50 border-indigo-500 text-indigo-600' : 'bg-white border-gray-300 text-gray-500 hover:bg-gray-50'
                              }`}>
                              {pageNum}
                            </button>
                          );
                        })}
                        <button onClick={() => setPage(page + 1)} disabled={page >= totalPages}
                          className="inline-flex items-center px-2 py-2 border border-gray-300 text-sm rounded-md text-gray-500 bg-white hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed">
                          <ChevronRight className="h-4 w-4" />
                        </button>
                      </div>
                    </div>
                  </div>
                </div>
              );
            })()}
          </div>
        )}
      </Card>

      {/* Create Single Code Modal */}
      {showCreateModal && (
        <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50">
          <div className="relative top-20 mx-auto p-5 border w-full max-w-md shadow-lg rounded-md bg-white">
            <div className="flex justify-between items-center mb-4">
              <h3 className="text-lg font-medium">Create Promo Code</h3>
              <button 
                onClick={() => {
                  setShowCreateModal(false);
                  setModalError('');
                }} 
                className="text-gray-400 hover:text-gray-600"
              >
                ×
              </button>
            </div>
            {modalError && (
              <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-md">
                <p className="text-sm text-red-700">{modalError}</p>
              </div>
            )}
            <form onSubmit={handleCreateSingle} className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700">Code <span className="text-red-500">*</span></label>
                <input
                  type="text"
                  required
                  value={formData.code}
                  onChange={(e) => setFormData({ ...formData, code: e.target.value.toUpperCase() })}
                  placeholder="e.g., SUMMER20"
                  className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-alps-primary focus:border-alps-primary sm:text-sm"
                />
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700">Discount Type <span className="text-red-500">*</span></label>
                  <select
                    required
                    value={formData.discount_type}
                    onChange={(e) => setFormData({ ...formData, discount_type: e.target.value as 'fixed' | 'percentage' })}
                    className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-alps-primary focus:border-alps-primary sm:text-sm"
                  >
                    <option value="fixed">Fixed Amount (€)</option>
                    <option value="percentage">Percentage (%)</option>
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700">Value <span className="text-red-500">*</span></label>
                  <input
                    type="number"
                    required
                    min="0"
                    step="0.01"
                    value={formData.discount_value === '' ? '' : formData.discount_value}
                    onChange={(e) => {
                      const value = e.target.value;
                      setFormData({ 
                        ...formData, 
                        discount_value: value === '' ? '' : parseFloat(value) || ''
                      });
                    }}
                    className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-alps-primary focus:border-alps-primary sm:text-sm"
                  />
                </div>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700">Min Subtotal Amount</label>
                <input
                  type="number"
                  min="0"
                  step="0.01"
                  value={formData.min_order_amount}
                  onChange={(e) => setFormData({ ...formData, min_order_amount: parseFloat(e.target.value) || 0 })}
                  className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-alps-primary focus:border-alps-primary sm:text-sm"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700">Max Subtotal Amount</label>
                <input
                  type="number"
                  min="0"
                  step="0.01"
                  value={formData.max_order_amount}
                  onChange={(e) => setFormData({ ...formData, max_order_amount: parseFloat(e.target.value) || 0 })}
                  className={`mt-1 block w-full border rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-alps-primary focus:border-alps-primary sm:text-sm ${formData.max_order_amount > 0 && formData.min_order_amount > 0 && formData.max_order_amount < formData.min_order_amount ? 'border-red-500' : 'border-gray-300'}`}
                />
                {formData.max_order_amount > 0 && formData.min_order_amount > 0 && formData.max_order_amount < formData.min_order_amount ? (
                  <p className="mt-1 text-xs text-red-500">Max subtotal amount cannot be less than min subtotal amount</p>
                ) : (
                  <p className="mt-1 text-xs text-gray-500">0 = no limit</p>
                )}
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700">Merchant</label>
                <MultiSelect
                  options={merchantOptions}
                  selected={formData.selectedMerchants}
                  onChange={(selected) => setFormData({ ...formData, selectedMerchants: selected })}
                  placeholder="All merchants"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700">Resorts</label>
                <MultiSelect
                  options={resortOptions}
                  selected={formData.selectedResorts}
                  onChange={(selected) => setFormData({ ...formData, selectedResorts: selected })}
                  placeholder="All resorts"
                />
                <p className="mt-1 text-xs text-gray-500">Valid for all merchants in selected resorts</p>
              </div>
              <div>
                <label className="flex items-center space-x-2 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={formData.max_uses_per_user !== 0}
                    onChange={(e) => setFormData({ ...formData, max_uses_per_user: e.target.checked ? '' : 0 })}
                    className="rounded border-gray-300 text-alps-primary focus:ring-alps-primary"
                  />
                  <span className="text-sm font-medium text-gray-700">Multi-use code</span>
                </label>
                {formData.max_uses_per_user !== 0 ? (
                  <div className="mt-2">
                    <label className="flex items-center space-x-2 cursor-pointer">
                      <input
                        type="checkbox"
                        checked={formData.max_uses_per_user === -1}
                        onChange={(e) => setFormData({ ...formData, max_uses_per_user: e.target.checked ? -1 : '' })}
                        className="rounded border-gray-300 text-alps-primary focus:ring-alps-primary"
                      />
                      <span className="text-sm text-gray-700">Infinite use</span>
                    </label>
                    {formData.max_uses_per_user !== -1 && (
                      <div className="mt-2">
                        <label className="block text-xs text-gray-500 mb-1">Uses per user</label>
                        <input
                          type="number"
                          min="1"
                          step="1"
                          value={formData.max_uses_per_user === '' ? '' : formData.max_uses_per_user}
                          placeholder="Set how many times each user can use this code"
                          onChange={(e) => {
                            const val = e.target.value;
                            setFormData({ ...formData, max_uses_per_user: val === '' ? '' : Math.max(1, parseInt(val) || 1) });
                          }}
                          className="block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-alps-primary focus:border-alps-primary sm:text-sm"
                        />
                      </div>
                    )}
                  </div>
                ) : (
                  <p className="mt-1 text-xs text-gray-500">The code can be used only once</p>
                )}
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700">Start Date</label>
                  <input
                    type="date"
                    min={new Date().toISOString().split('T')[0]}
                    value={formData.start_date}
                    onChange={(e) => setFormData({ ...formData, start_date: e.target.value })}
                    className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-alps-primary focus:border-alps-primary sm:text-sm"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700">End Date</label>
                  <input
                    type="date"
                    min={new Date().toISOString().split('T')[0]}
                    value={formData.end_date}
                    onChange={(e) => setFormData({ ...formData, end_date: e.target.value })}
                    className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-alps-primary focus:border-alps-primary sm:text-sm"
                  />
                </div>
              </div>
              <p className="text-xs text-gray-500 -mt-2">Leave empty for no expiration</p>
              <div>
                <label className="block text-sm font-medium text-gray-700">Status</label>
                <select
                  value={formData.status}
                  onChange={(e) => setFormData({ ...formData, status: e.target.value as 'Active' | 'Inactive' })}
                  className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-alps-primary focus:border-alps-primary sm:text-sm"
                >
                  <option value="Active">Active</option>
                  <option value="Inactive">Inactive</option>
                </select>
              </div>
              <div className="flex justify-end space-x-2 pt-4">
                <button
                  type="button"
                  onClick={() => {
                    setShowCreateModal(false);
                    setModalError('');
                  }}
                  className="px-4 py-2 border border-gray-300 rounded-md text-gray-700 hover:bg-gray-50"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  className="px-4 py-2 bg-alps-primary text-white rounded-md hover:bg-alps-primary-dark"
                >
                  Create
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Bulk Generate Modal */}
      {showBulkModal && (
        <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50">
          <div className="relative top-20 mx-auto p-5 border w-full max-w-md shadow-lg rounded-md bg-white">
            <div className="flex justify-between items-center mb-4">
              <h3 className="text-lg font-medium">Bulk Generate Promo Codes</h3>
              <button 
                onClick={() => {
                  setShowBulkModal(false);
                  setBulkModalError('');
                }} 
                className="text-gray-400 hover:text-gray-600"
              >
                ×
              </button>
            </div>
            {bulkModalError && (
              <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-md">
                <p className="text-sm text-red-700">{bulkModalError}</p>
              </div>
            )}
            <form onSubmit={handleBulkGenerate} className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700">Number of Codes <span className="text-red-500">*</span></label>
                  <input
                    type="number"
                    required
                    min="1"
                    max="1000"
                    value={bulkFormData.count}
                    onChange={(e) => setBulkFormData({ ...bulkFormData, count: parseInt(e.target.value) })}
                    className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-alps-primary focus:border-alps-primary sm:text-sm"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700">Code Prefix</label>
                  <input
                    type="text"
                    maxLength={4}
                    value={bulkFormData.code_prefix}
                    onChange={(e) => setBulkFormData({ ...bulkFormData, code_prefix: e.target.value.toUpperCase() })}
                    placeholder="e.g., SUM"
                    className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-alps-primary focus:border-alps-primary sm:text-sm"
                  />
                </div>
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700">Discount Type <span className="text-red-500">*</span></label>
                  <select
                    required
                    value={bulkFormData.discount_type}
                    onChange={(e) => setBulkFormData({ ...bulkFormData, discount_type: e.target.value as 'fixed' | 'percentage' })}
                    className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-alps-primary focus:border-alps-primary sm:text-sm"
                  >
                    <option value="fixed">Fixed Amount (€)</option>
                    <option value="percentage">Percentage (%)</option>
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700">Value <span className="text-red-500">*</span></label>
                  <input
                    type="number"
                    required
                    min="0"
                    step="0.01"
                    value={bulkFormData.discount_value === '' ? '' : bulkFormData.discount_value}
                    onChange={(e) => {
                      const value = e.target.value;
                      setBulkFormData({ 
                        ...bulkFormData, 
                        discount_value: value === '' ? '' : parseFloat(value) || ''
                      });
                    }}
                    className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-alps-primary focus:border-alps-primary sm:text-sm"
                  />
                </div>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700">Min Subtotal Amount</label>
                <input
                  type="number"
                  min="0"
                  step="0.01"
                  value={bulkFormData.min_order_amount}
                  onChange={(e) => setBulkFormData({ ...bulkFormData, min_order_amount: parseFloat(e.target.value) || 0 })}
                  className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-alps-primary focus:border-alps-primary sm:text-sm"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700">Max Subtotal Amount</label>
                <input
                  type="number"
                  min="0"
                  step="0.01"
                  value={bulkFormData.max_order_amount}
                  onChange={(e) => setBulkFormData({ ...bulkFormData, max_order_amount: parseFloat(e.target.value) || 0 })}
                  className={`mt-1 block w-full border rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-alps-primary focus:border-alps-primary sm:text-sm ${bulkFormData.max_order_amount > 0 && bulkFormData.min_order_amount > 0 && bulkFormData.max_order_amount < bulkFormData.min_order_amount ? 'border-red-500' : 'border-gray-300'}`}
                />
                {bulkFormData.max_order_amount > 0 && bulkFormData.min_order_amount > 0 && bulkFormData.max_order_amount < bulkFormData.min_order_amount ? (
                  <p className="mt-1 text-xs text-red-500">Max subtotal amount cannot be less than min subtotal amount</p>
                ) : (
                  <p className="mt-1 text-xs text-gray-500">0 = no limit</p>
                )}
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700">Merchant</label>
                <MultiSelect
                  options={merchantOptions}
                  selected={bulkFormData.selectedMerchants}
                  onChange={(selected) => setBulkFormData({ ...bulkFormData, selectedMerchants: selected })}
                  placeholder="All merchants"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700">Resorts</label>
                <MultiSelect
                  options={resortOptions}
                  selected={bulkFormData.selectedResorts}
                  onChange={(selected) => setBulkFormData({ ...bulkFormData, selectedResorts: selected })}
                  placeholder="All resorts"
                />
                <p className="mt-1 text-xs text-gray-500">Valid for all merchants in selected resorts</p>
              </div>
              <div>
                <label className="flex items-center space-x-2 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={bulkFormData.max_uses_per_user !== 0}
                    onChange={(e) => setBulkFormData({ ...bulkFormData, max_uses_per_user: e.target.checked ? '' : 0 })}
                    className="rounded border-gray-300 text-alps-primary focus:ring-alps-primary"
                  />
                  <span className="text-sm font-medium text-gray-700">Multi-use code</span>
                </label>
                {bulkFormData.max_uses_per_user !== 0 ? (
                  <div className="mt-2">
                    <label className="flex items-center space-x-2 cursor-pointer">
                      <input
                        type="checkbox"
                        checked={bulkFormData.max_uses_per_user === -1}
                        onChange={(e) => setBulkFormData({ ...bulkFormData, max_uses_per_user: e.target.checked ? -1 : '' })}
                        className="rounded border-gray-300 text-alps-primary focus:ring-alps-primary"
                      />
                      <span className="text-sm text-gray-700">Infinite use</span>
                    </label>
                    {bulkFormData.max_uses_per_user !== -1 && (
                      <div className="mt-2">
                        <label className="block text-xs text-gray-500 mb-1">Uses per user</label>
                        <input
                          type="number"
                          min="1"
                          step="1"
                          value={bulkFormData.max_uses_per_user === '' ? '' : bulkFormData.max_uses_per_user}
                          placeholder="Set how many times each user can use this code"
                          onChange={(e) => {
                            const val = e.target.value;
                            setBulkFormData({ ...bulkFormData, max_uses_per_user: val === '' ? '' : Math.max(1, parseInt(val) || 1) });
                          }}
                          className="block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-alps-primary focus:border-alps-primary sm:text-sm"
                        />
                      </div>
                    )}
                  </div>
                ) : (
                  <p className="mt-1 text-xs text-gray-500">The code can be used only once</p>
                )}
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700">Start Date</label>
                  <input
                    type="date"
                    min={new Date().toISOString().split('T')[0]}
                    value={bulkFormData.start_date}
                    onChange={(e) => setBulkFormData({ ...bulkFormData, start_date: e.target.value })}
                    className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-alps-primary focus:border-alps-primary sm:text-sm"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700">End Date</label>
                  <input
                    type="date"
                    min={new Date().toISOString().split('T')[0]}
                    value={bulkFormData.end_date}
                    onChange={(e) => setBulkFormData({ ...bulkFormData, end_date: e.target.value })}
                    className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-alps-primary focus:border-alps-primary sm:text-sm"
                  />
                </div>
              </div>
              <p className="text-xs text-gray-500 -mt-2">Leave empty for no expiration</p>
              <div className="flex justify-end space-x-2 pt-4">
                <button
                  type="button"
                  onClick={() => {
                    setShowBulkModal(false);
                    setBulkModalError('');
                  }}
                  className="px-4 py-2 border border-gray-300 rounded-md text-gray-700 hover:bg-gray-50"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  className="px-4 py-2 bg-alps-primary text-white rounded-md hover:bg-alps-primary-dark"
                >
                  Generate
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

